import { client, freshClient } from './sanity';
import type {
  GalleryCategoryData,
  GalleryItemData,
  ProductCardData,
  ProductDetailData,
  ProjectCardData,
  ProjectDetailData,
  ServiceCardData,
  ServiceDetailData,
} from '@/types/sanity';

/**
 * All Sanity reads used by the site live here so that:
 *  - queries are typed and reviewed in one place,
 *  - list queries only select the fields a card actually renders,
 *  - every fetch can be cancelled with an `AbortSignal` (unmount / route change).
 */

/**
 * Projection for anything that renders through `<SanityImage>`.
 * The asset stays a reference; we only add the small blur placeholder and the
 * intrinsic dimensions (used to reserve layout space and avoid content shift).
 */
const IMAGE = `{
  asset,
  crop,
  hotspot,
  "lqip": asset->metadata.lqip,
  "dimensions": asset->metadata.dimensions
}`;

interface FetchOptions {
  signal?: AbortSignal;
}

/* ------------------------------------------------------------------ */
/* Products                                                            */
/* ------------------------------------------------------------------ */

export function fetchProductCards({ signal }: FetchOptions = {}) {
  return client.fetch<ProductCardData[]>(
    `*[_type == "product" && defined(slug.current)] | order(title asc) {
      "slug": slug.current,
      "linkUrl": "/products/" + slug.current,
      "cardTitle": title,
      "cardContent": description,
      "category": category->title,
      "subcategory": subcategory,
      "image": thumbnail${IMAGE}
    }`,
    {},
    { signal }
  );
}

export async function fetchProductCategories({ signal }: FetchOptions = {}): Promise<string[]> {
  const categories = await client.fetch<{ title: string }[]>(
    `*[_type == "category" && defined(title)] | order(title asc) { title }`,
    {},
    { signal }
  );
  return Array.from(new Set(categories.map((category) => category.title)));
}

export function fetchProductDetail(slug: string, { signal }: FetchOptions = {}) {
  return client.fetch<ProductDetailData | null>(
    `*[_type == "product" && slug.current == $slug][0] {
      title,
      "image": thumbnail${IMAGE},
      description,
      advantages,
      uses,
      propertyTable,
      "category": category->title,
      packing,
      advantagesNote,
      usesNote
    }`,
    { slug },
    { signal }
  );
}

/* ------------------------------------------------------------------ */
/* Services                                                            */
/* ------------------------------------------------------------------ */

export function fetchServiceCards({ signal }: FetchOptions = {}) {
  return client.fetch<ServiceCardData[]>(
    `*[_type == "service" && defined(slug.current)] | order(title asc) {
      "linkUrl": "/services/" + slug.current,
      "cardTitle": title,
      "cardContent": description,
      "image": thumbnail${IMAGE}
    }`,
    {},
    { signal }
  );
}

export function fetchServiceDetail(slug: string, { signal }: FetchOptions = {}) {
  return client.fetch<ServiceDetailData | null>(
    `*[_type == "service" && slug.current == $slug][0] {
      title,
      "image": thumbnail${IMAGE},
      description,
      advantages,
      applications,
      types,
      installation,
      whyChooseUs,
      whyChooseUsPoints,
      customBullets,
      additionalInfo
    }`,
    { slug },
    { signal }
  );
}

/* ------------------------------------------------------------------ */
/* Projects                                                            */
/* ------------------------------------------------------------------ */

/** Pass a category (e.g. "Completed Projects") to filter, or omit for all projects. */
export function fetchProjectCards(
  category: string | null = null,
  { signal }: FetchOptions = {}
) {
  return client.fetch<ProjectCardData[]>(
    `*[_type == "project" && (!defined($category) || category == $category)] {
      _id,
      _updatedAt,
      title,
      category,
      subCategory,
      "slug": slug.current,
      "image": image${IMAGE}
    }`,
    { category },
    { signal }
  );
}

export function fetchProjectDetail(slug: string, { signal }: FetchOptions = {}) {
  return client.fetch<ProjectDetailData | null>(
    `*[_type == "project" && slug.current == $slug][0] {
      title,
      category,
      "slug": slug.current,
      "image": image${IMAGE},
      location,
      client,
      scopeOfWork,
      commencement,
      finalizationDate
    }`,
    { slug },
    { signal }
  );
}

/* ------------------------------------------------------------------ */
/* Gallery                                                             */
/* ------------------------------------------------------------------ */

/**
 * Images are returned as references (resized on demand via the image pipeline);
 * videos are plain files, so we only need their URL and MIME type.
 */
export function fetchGalleryItems({ signal }: FetchOptions = {}) {
  return freshClient.fetch<GalleryItemData[]>(
    `*[_type == "galleryItem"] {
      _id,
      title,
      order,
      uploadedAt,
      category->{ _id, title, description, "slug": slug.current },
      mediaFiles[] {
        _type,
        _type == "image" => {
          asset,
          crop,
          hotspot,
          "lqip": asset->metadata.lqip,
          "dimensions": asset->metadata.dimensions
        },
        _type == "file" => {
          "url": asset->url,
          "mimeType": asset->mimeType
        }
      }
    }`,
    {},
    { signal }
  );
}

/**
 * Every gallery category that exists in Sanity, regardless of whether any item currently uses
 * it — so a category page can tell "this category is real, just empty so far" apart from "this
 * URL doesn't correspond to anything".
 */
export function fetchGalleryCategories({ signal }: FetchOptions = {}) {
  return freshClient.fetch<GalleryCategoryData[]>(
    `*[_type == "galleryCategory" && defined(slug.current)] | order(title asc) {
      _id,
      title,
      description,
      "slug": slug.current
    }`,
    {},
    { signal }
  );
}
